import indexedDBService from './IndexedDBService';

class MigrationService {
  constructor() {
    this.migrationKey = 'indexeddb_migration_completed';
    this.cleanupKey = 'localstorage_cleanup_completed';
  }

  async shouldMigrate() {
    const migrationCompleted = await indexedDBService.getSetting(this.migrationKey);
    return !migrationCompleted;
  }

  async shouldCleanup() {
    const cleanupCompleted = await indexedDBService.getSetting(this.cleanupKey);
    return !cleanupCompleted;
  }

  async performMigration() {
    try {
      console.log('Starting migration from localStorage to IndexedDB...');
      
      // Check if IndexedDB is supported
      if (!indexedDBService.constructor.isSupported()) {
        console.log('IndexedDB not supported, skipping migration');
        return;
      }

      // Perform the migration
      await indexedDBService.migrateFromLocalStorage();
      
      // Verify critical data was migrated
      const userDetails = await indexedDBService.getUserData('userDetails');
      if (!userDetails) {
        console.error('Critical data (userDetails) not found after migration');
        throw new Error('Migration verification failed - userDetails not found');
      }
      
      console.log('Migration verification passed - userDetails found in IndexedDB');
      
      // Mark migration as completed
      await indexedDBService.setSetting(this.migrationKey, true);
      
      console.log('Migration completed successfully');
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    }
  }

  async performCleanup() {
    try {
      console.log('Starting localStorage cleanup...');
      
      // Verify that IndexedDB migration was successful
      const migrationCompleted = await indexedDBService.getSetting(this.migrationKey);
      console.log('Migration completed flag:', migrationCompleted);
      
      if (!migrationCompleted) {
        console.log('Migration not completed, skipping cleanup');
        return;
      }

      // Verify that critical data exists in IndexedDB
      const userDetails = await indexedDBService.getUserData('userDetails');
      console.log('User details in IndexedDB:', !!userDetails);
      
      if (!userDetails) {
        console.log('Critical data not found in IndexedDB, skipping cleanup');
        return;
      }

      // List of localStorage keys to clean up
      const keysToCleanup = [
        'userDetails',
        'idToken',
        'refreshToken',
        'language',
        'theme',
        'showWelcome',
        'locationGranted',
        'activeOrders',
        'activeRide',
        'orderDishes',
        'restaurants',
        'selectedRestaurant',
        'selectedCategory',
        'orderCheckout',
        'auth'
      ];

      // Clean up each key
      let cleanedCount = 0;
      for (const key of keysToCleanup) {
        try {
          if (localStorage.getItem(key) !== null) {
            localStorage.removeItem(key);
            cleanedCount++;
            console.log(`Cleaned up localStorage key: ${key}`);
          }
        } catch (error) {
          console.warn(`Failed to clean up localStorage key ${key}:`, error);
        }
      }

      // Mark cleanup as completed
      await indexedDBService.setSetting(this.cleanupKey, true);
      
      console.log(`localStorage cleanup completed. Cleaned ${cleanedCount} keys.`);
    } catch (error) {
      console.error('localStorage cleanup failed:', error);
      throw error;
    }
  }

  async initializeApp() {
    try {
      // Check if migration is needed
      if (await this.shouldMigrate()) {
        await this.performMigration();
      }
      
      // Check if cleanup is needed
      if (await this.shouldCleanup()) {
        await this.performCleanup();
      }
      
      // Initialize IndexedDB
      await indexedDBService.init();
      
      console.log('App initialization completed');
    } catch (error) {
      console.error('App initialization failed:', error);
      // Continue with localStorage fallback
    }
  }

  // Helper method to get data with fallback
  async getDataWithFallback(key, storeType = 'userData') {
    try {
      // Try IndexedDB first
      const data = await indexedDBService[`get${storeType.charAt(0).toUpperCase() + storeType.slice(1)}`](key);
      if (data !== null && data !== undefined) {
        return data;
      }
    } catch (error) {
      console.warn(`Failed to get ${key} from IndexedDB:`, error);
    }

    // Fallback to localStorage
    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        return JSON.parse(value);
      }
    } catch (error) {
      console.warn(`Failed to get ${key} from localStorage:`, error);
    }

    return null;
  }

  // Helper method to set data with fallback
  async setDataWithFallback(key, value, storeType = 'userData') {
    try {
      // Try IndexedDB first
      await indexedDBService[`set${storeType.charAt(0).toUpperCase() + storeType.slice(1)}`](key, value);
    } catch (error) {
      console.warn(`Failed to set ${key} in IndexedDB:`, error);
      // Fallback to localStorage
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (localStorageError) {
        console.error(`Failed to set ${key} in localStorage:`, localStorageError);
      }
    }
  }

  // Method to manually trigger cleanup (for testing or manual execution)
  async manualCleanup() {
    try {
      await this.performCleanup();
      console.log('Manual cleanup completed successfully');
    } catch (error) {
      console.error('Manual cleanup failed:', error);
      throw error;
    }
  }

  // Method to check cleanup status
  async getCleanupStatus() {
    const migrationCompleted = await indexedDBService.getSetting(this.migrationKey);
    const cleanupCompleted = await indexedDBService.getSetting(this.cleanupKey);
    
    return {
      migrationCompleted: !!migrationCompleted,
      cleanupCompleted: !!cleanupCompleted,
      needsMigration: !migrationCompleted,
      needsCleanup: migrationCompleted && !cleanupCompleted
    };
  }

  // Method to reset migration flags (for testing)
  async resetMigrationFlags() {
    try {
      await indexedDBService.removeSetting(this.migrationKey);
      await indexedDBService.removeSetting(this.cleanupKey);
      console.log('Migration flags reset successfully');
    } catch (error) {
      console.error('Failed to reset migration flags:', error);
      throw error;
    }
  }

  // Method to force cleanup (bypasses migration check)
  async forceCleanup() {
    try {
      console.log('Force cleaning localStorage...');
      
      // List of localStorage keys to clean up
      const keysToCleanup = [
        'userDetails',
        'idToken',
        'refreshToken',
        'language',
        'theme',
        'showWelcome',
        'locationGranted',
        'activeOrders',
        'activeRide',
        'orderDishes',
        'restaurants',
        'selectedRestaurant',
        'selectedCategory',
        'orderCheckout',
        'auth'
      ];

      // Clean up each key
      let cleanedCount = 0;
      for (const key of keysToCleanup) {
        try {
          if (localStorage.getItem(key) !== null) {
            localStorage.removeItem(key);
            cleanedCount++;
            console.log(`Force cleaned localStorage key: ${key}`);
          }
        } catch (error) {
          console.warn(`Failed to force clean localStorage key ${key}:`, error);
        }
      }

      // Mark cleanup as completed
      await indexedDBService.setSetting(this.cleanupKey, true);
      
      console.log(`Force localStorage cleanup completed. Cleaned ${cleanedCount} keys.`);
    } catch (error) {
      console.error('Force localStorage cleanup failed:', error);
      throw error;
    }
  }

  // Debug method to check current state
  async debugMigrationState() {
    try {
      const migrationCompleted = await indexedDBService.getSetting(this.migrationKey);
      const cleanupCompleted = await indexedDBService.getSetting(this.cleanupKey);
      const userDetails = await indexedDBService.getUserData('userDetails');
      
      const localStorageKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          localStorageKeys.push(key);
        }
      }

      console.log('=== Migration Debug Info ===');
      console.log('Migration completed:', migrationCompleted);
      console.log('Cleanup completed:', cleanupCompleted);
      console.log('User details in IndexedDB:', !!userDetails);
      console.log('localStorage keys:', localStorageKeys);
      console.log('localStorage size:', localStorageKeys.length);
      
      return {
        migrationCompleted: !!migrationCompleted,
        cleanupCompleted: !!cleanupCompleted,
        userDetailsInIndexedDB: !!userDetails,
        localStorageKeys,
        localStorageSize: localStorageKeys.length
      };
    } catch (error) {
      console.error('Debug failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const migrationService = new MigrationService();

export default migrationService; 